import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppSelector } from '@/store/hooks'
import { taskPath } from '@/utils/taskPath'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { selectAllTasks } from '@/store/tasks/tasksSelectors'
import { selectAllProjects, selectProjectsById } from '@/store/projects/projectsSelectors'
import { getRecentTasks, searchTasksAndProjects, type SearchResult } from '@/utils/globalSearch'
import SearchIcon from '@/assets/icons/icon-search.svg?react'
import CloseIcon from '@/assets/icons/icon-close.svg?react'
import ProjectIcon from '@/assets/icons/icon-project.svg?react'
import css from './GlobalSearchModal.module.css'

const RECENT_COUNT = 6

interface GlobalSearchModalProps {
    onClose: () => void
}

const GlobalSearchModal = ({ onClose }: GlobalSearchModalProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { workspace } = useWorkspace()
    const tasks = useAppSelector(selectAllTasks)
    const projects = useAppSelector(selectAllProjects)
    const projectsById = useAppSelector(selectProjectsById)

    const [query, setQuery] = useState('')
    const [highlighted, setHighlighted] = useState(0)
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const paletteRef = useRef<HTMLDivElement>(null)

    const trimmed = query.trim()

    const results: SearchResult[] = useMemo(() => {
        if (!trimmed) return getRecentTasks(tasks, RECENT_COUNT)
        return searchTasksAndProjects(trimmed, tasks, projects)
    }, [trimmed, tasks, projects])

    useEffect(() => { setHighlighted(0) }, [trimmed])
    useEffect(() => { inputRef.current?.focus() }, [])

    // Keep the active row in view when navigating with the keyboard.
    useEffect(() => {
        const el = listRef.current?.children[highlighted] as HTMLElement | undefined
        el?.scrollIntoView({ block: 'nearest' })
    }, [highlighted])

    // Close on Escape (the input keydown handles arrows/Enter).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])

    const prefix = workspace?.workspaceTaskPrefixId ?? ''

    const openResult = (result: SearchResult) => {
        if (!workspace) return
        if (result.kind === 'task') {
            const path = taskPath({ workspaceUrlId: workspace.workspaceUrlId, prefix: workspace.workspaceTaskPrefixId, number: result.number })
            if (path) navigate(path, { state: { backgroundLocation: location } })
        } else {
            navigate(`/${workspace.workspaceUrlId}/projects/${result.id}`)
        }
        onClose()
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlighted(i => Math.min(results.length - 1, i + 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlighted(i => Math.max(0, i - 1))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            const result = results[highlighted]
            if (result) openResult(result)
        }
    }

    const handleOverlayMouseDown = (e: React.MouseEvent) => {
        if (paletteRef.current && !paletteRef.current.contains(e.target as Node)) onClose()
    }

    const showSectionLabel = results.length > 0
    const sectionLabel = trimmed ? 'Results' : 'Recent'
    const showEmpty = !!trimmed && results.length === 0

    return (
        <div className={css.overlay} onMouseDown={handleOverlayMouseDown}>
            <div className={css.palette} ref={paletteRef}>
                <div className={css.header}>
                    <SearchIcon className={css.search_icon} />
                    <input
                        ref={inputRef}
                        className={css.input}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search tasks and projects…"
                    />
                    <button className={css.close} onClick={onClose} aria-label="Close">
                        <CloseIcon />
                    </button>
                </div>

                {showSectionLabel && <div className={css.section}>{sectionLabel}</div>}

                {showEmpty && <div className={css.empty}>No results</div>}

                <ul className={css.list} ref={listRef}>
                    {results.map((result, index) => {
                        const isActive = index === highlighted
                        const rowClass = isActive ? `${css.row} ${css.row_active}` : css.row
                        if (result.kind === 'task') {
                            const projectName = projectsById[result.projectId]?.name || ''
                            return (
                                <li
                                    key={`task-${result.id}`}
                                    className={rowClass}
                                    onMouseMove={() => setHighlighted(index)}
                                    onClick={() => openResult(result)}
                                >
                                    <span className={css.row_main}>
                                        <span className={css.row_id}>{prefix}-{result.number}</span>
                                        <span className={css.row_name}>{result.name || 'Untitled'}</span>
                                    </span>
                                    {projectName && <span className={css.row_sub}>{projectName}</span>}
                                </li>
                            )
                        }
                        return (
                            <li
                                key={`project-${result.id}`}
                                className={rowClass}
                                onMouseMove={() => setHighlighted(index)}
                                onClick={() => openResult(result)}
                            >
                                <span className={css.row_main}>
                                    <ProjectIcon className={css.row_project_icon} />
                                    <span className={css.row_name}>{result.name || 'Untitled'}</span>
                                </span>
                                <span className={css.row_sub}>Project</span>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </div>
    )
}

export default GlobalSearchModal
